// 개인정보 처리방침 · 이용약관 (KR/EN/JP).
// ⚠️ 법률 자문 아님 — 출시 전 각 시장 변호사 검토 필수.

import type { Lang } from "../lib/types";

export interface LegalSection {
  h: string;
  body: string[]; // 불릿은 "· "로 시작.
}
export interface LegalDoc {
  title: string;
  effective: string;
  intro: string[];
  sections: LegalSection[];
}
export type LegalKind = "privacy" | "terms";

const EFFECTIVE = { ko: "시행일: 2026년 6월 23일", en: "Effective date: June 23, 2026", ja: "施行日: 2026年6月23日" };

export const LEGAL: Record<Lang, Record<LegalKind, LegalDoc>> = {
  ko: {
    privacy: {
      title: "개인정보 처리방침",
      effective: EFFECTIVE.ko,
      intro: [
        'Gorocket Company Inc.(이하 "회사")은 Hyuh(이하 "서비스") 이용자의 개인정보를 중요하게 생각하며, 「개인정보 보호법」 등 관련 법령을 준수합니다.',
      ],
      sections: [
        { h: "1. 기본 원칙", body: ["서비스는 계정·로그인이 없으며, 이름·이메일 등 식별정보를 수집하지 않습니다. 이용자가 작성·발화한 고백은 서버에 저장하지 않습니다."] },
        {
          h: "2. 수집·처리하는 정보",
          body: [
            "· 고백 내용: 이용자가 작성하거나 음성으로 말한 텍스트. 달의 편지·한 마디·예언 생성과 안전(위기) 판정을 위해 일시적으로 처리된 뒤 폐기됩니다.",
            "· 음성: 마이크 사용 시, 기기 운영체제의 음성 인식 서비스(iOS는 Apple, Android는 Google·단말 제조사)가 음성을 텍스트로 변환합니다. 이 과정에서 해당 서비스가 오디오를 자사 서버로 전송해 처리할 수 있습니다. 회사(앱)는 오디오를 녹음·저장·전송하지 않으며, 변환된 텍스트만 처리합니다.",
            "· 광고·기기 정보: 광고 제공을 위해 광고 식별자, IP, 기기 정보 등이 광고 파트너를 통해 처리될 수 있습니다.",
            "· 로컬 데이터: 언어·소리 설정 등 일부 정보는 이용자 기기에만 저장됩니다(서버 전송 없음).",
          ],
        },
        { h: "3. 이용 목적", body: ["달의 편지·한 마디·예언 생성, 안전 판정(자해·위기 신호 감지 시 지원 정보 안내), 광고 제공, 기기 내 기능 제공."] },
        { h: "4. 저장하지 않는 것 / 학습에 쓰지 않는 것", body: ["고백 내용은 처리 후 폐기되며 서버에 저장하지 않습니다. 고백 내용은 AI 모델 학습에 사용하지 않습니다. 회사는 개인정보를 판매하지 않습니다."] },
        {
          h: "응답 신고 시 수집하는 정보",
          body: [
            "Hyuh는 기본적으로 이용자의 고백 내용이나 생성된 응답을 저장하지 않습니다. 다만 이용자가 특정 응답을 직접 \"신고\"하는 경우에 한해, 서비스 품질 및 안전성 개선을 위해 아래 정보를 수집·저장합니다.",
            "· 신고한 AI 응답의 원문",
            "· 해당 응답 직전에 이용자가 입력한 텍스트",
            "· 신고 사유 및 이용자가 직접 작성한 의견(선택)",
            "· 앱 버전, 플랫폼, 언어 설정, 신고 일시",
            "이 수집은 이용자가 신고를 전송하는 경우에만 이루어지며, 신고하지 않은 고백 및 응답은 저장되지 않습니다. 수집된 정보는 AI 응답 품질과 안전 기능 개선 목적으로만 사용되며, 광고나 이용자 추적 목적으로 사용되지 않습니다. Hyuh는 별도의 계정·로그인을 요구하지 않으므로 이 정보는 이용자의 신원과 연결되지 않습니다. 수집된 정보는 12개월 동안 보관된 후 삭제됩니다.",
          ],
        },
        {
          h: "5. 처리위탁·제3자",
          body: ["· AI 처리: Google Cloud Vertex AI — Gemini 2.5 Flash(편지·한 마디 생성 및 안전 판정). 고백 텍스트는 회사 백엔드(Google Cloud Run)를 거쳐 전송되며 응답 후 폐기됩니다.", "· 음성 변환(STT): 기기 운영체제의 음성 인식 서비스(iOS=Apple, Android=Google·단말 제조사). 오디오 처리는 해당 OS·제공자가 수행하며, 회사는 오디오를 보관하지 않습니다.", "· 광고: Google AdMob(앱) / Google AdSense(웹).", "각 사의 처리방침은 해당 사 정책을 따릅니다."],
        },
        { h: "6. 광고와 동의", body: ["광고 파트너는 광고 제공을 위해 정보를 수집·이용할 수 있습니다. EEA·영국 등에서는 동의에 따라, iOS에서는 앱 추적 투명성(ATT)에 따라 처리됩니다. 광고 맞춤설정은 기기 설정에서 제한할 수 있습니다."] },
        { h: "7. 보유 기간", body: ["고백 내용: 처리 직후 폐기(미보관). 로컬 설정: 이용자가 삭제하기 전까지 기기에 보관. 광고·분석 데이터: 각 파트너 정책에 따름."] },
        { h: "8. 이용자 권리", body: ["이용자는 관련 법령에 따라 열람·정정·삭제·처리정지 등을 요청할 수 있습니다. 서비스는 서버에 개인정보를 보관하지 않으므로, 로컬 데이터는 기기에서 직접 삭제할 수 있습니다. 문의: support@gorocket.me."] },
        { h: "9. 아동의 개인정보", body: ["서비스는 만 14세 미만을 대상으로 하지 않습니다."] },
        { h: "10. 국외 이전", body: ["AI·광고 처리 과정에서 정보가 국외(미국 등)에서 처리될 수 있습니다."] },
        { h: "11. 안전성 확보 조치", body: ["전송 구간 암호화 등 합리적인 보호조치를 적용합니다."] },
        { h: "12. 변경", body: ["본 방침 변경 시 서비스 내 또는 웹사이트에 고지합니다."] },
        { h: "13. 문의", body: ["Gorocket Company Inc. · support@gorocket.me"] },
      ],
    },
    terms: {
      title: "이용약관",
      effective: EFFECTIVE.ko,
      intro: [],
      sections: [
        { h: "1. 동의", body: ["서비스를 이용함으로써 본 약관에 동의하게 됩니다."] },
        { h: "2. 서비스 정의", body: ["Hyuh는 가상의 화자 ‘오래된 달’이 이용자의 고백에 장문의 편지·한 마디·예언으로 답하는 엔터테인먼트 서비스입니다. ‘달’은 특정 종교·실제 신을 나타내지 않으며 점성술·운세가 아닙니다. 모든 응답은 재미를 위한 것입니다."] },
        { h: "3. 의료·상담 서비스 아님", body: ["Hyuh는 의료·심리상담·위기개입 서비스가 아니며 이를 대체하지 않습니다. 긴급하거나 위기 상황이라면 전문기관 또는 긴급 연락처에 연락하시기 바랍니다."] },
        { h: "4. 이용 자격·연령", body: ["만 14세 이상만 이용할 수 있습니다."] },
        { h: "5. 이용자의 책임", body: ["이용자는 자신이 입력한 내용에 책임이 있습니다. 타인의 개인정보, 불법·권리침해 콘텐츠를 입력하지 마십시오. 고백 내용은 처리 후 폐기됩니다."] },
        { h: "6. 자동 생성물", body: ["편지·한 마디·예언은 AI가 자동 생성하며 재미를 위한 것입니다. 예측도 조언도 아니고, 부정확하거나 의도와 다를 수 있습니다."] },
        { h: "7. 광고·제3자", body: ["서비스에는 광고가 포함되며, 제3자 서비스(광고·AI 등)가 사용됩니다. 제3자 서비스에는 해당 사 약관·정책이 적용됩니다."] },
        { h: "8. 지식재산권", body: ['서비스, "Hyuh" 명칭·로고 등 일체의 권리는 회사에 귀속됩니다. 이용자가 입력한 내용에 대한 권리는 이용자에게 있으나, 해당 내용은 처리 후 폐기됩니다.'] },
        { h: "9. 보증의 부인", body: ['서비스는 "있는 그대로" 제공되며, 특정 목적 적합성 등 어떠한 보증도 하지 않습니다.'] },
        { h: "10. 책임의 제한", body: ["관련 법령이 허용하는 범위에서, 회사는 서비스 이용으로 인한 간접·부수적 손해에 대해 책임지지 않습니다."] },
        { h: "11. 준거법·관할", body: ["본 약관은 대한민국 법을 준거법으로 하며, 회사의 본점 소재지를 관할하는 법원을 전속적 합의관할 법원으로 합니다."] },
        { h: "12. 문의", body: ["Gorocket Company Inc. · support@gorocket.me"] },
      ],
    },
  },
  en: {
    privacy: {
      title: "Privacy Policy",
      effective: EFFECTIVE.en,
      intro: ['Gorocket Company Inc. ("we") respects your privacy and complies with applicable laws (including CCPA/CPRA, GDPR where applicable).'],
      sections: [
        { h: "1. Core principle", body: ['Hyuh ("the Service") has no accounts or login and does not collect identifying information such as your name or email. What you confess in writing or by voice is not stored on our servers.'] },
        {
          h: "2. Information we process",
          body: [
            "· Confession content: the text you write or speak. Processed transiently to generate the moon's letter, one line, and prophecy, and to run a safety (crisis) check, then discarded.",
            "· Voice: when you use the mic, your device's operating-system speech recognition (Apple on iOS; Google or your device maker on Android) converts speech to text. That service may transmit audio to its own servers for processing. We (the app) do not record, store, or transmit audio; we process only the resulting text.",
            "· Advertising/device data: advertising identifiers, IP, and device information may be processed by ad partners to serve ads.",
            "· Local data: some settings (language, sound) are stored only on your device and not sent to us.",
          ],
        },
        { h: "3. How we use it", body: ["To generate the moon's letter, one line, and prophecy; for safety (detecting self-harm/crisis signals to show support resources); to serve ads; and for on-device features."] },
        { h: "4. What we don't do", body: ["We do not store your confession content on our servers; it is discarded after processing. We do not use it to train AI models. We do not sell your personal information."] },
        {
          h: "Information Collected When You Report a Response",
          body: [
            "By default, Hyuh does not store your confessions or the responses it generates. However, only when you choose to \"report\" a specific response, we collect and store the following to improve the quality and safety of the service:",
            "· The text of the reported AI response",
            "· The text you entered immediately before that response",
            "· The report reason and any optional comment you provide",
            "· App version, platform, language setting, and the date/time of the report",
            "This collection occurs only when you submit a report; confessions and responses you do not report are not stored. The information is used solely to improve the quality and safety of AI responses, and is not used for advertising or to track you. Because Hyuh does not require an account or login, this information is not linked to your identity. Reported information is retained for 12 months and then deleted.",
          ],
        },
        { h: "5. Processors / third parties", body: ["· AI processing: Google Cloud Vertex AI — Gemini 2.5 Flash (generates the letter and the one line, and runs safety checks). Your text is sent through our backend proxy (Google Cloud Run) and discarded after the response.", "· Speech-to-text: your device's OS speech-recognition service (Apple on iOS; Google or your device maker on Android). Audio is handled by that OS/provider; we do not retain audio.", "· Advertising: Google AdMob (apps) / Google AdSense (web).", "Their handling is governed by their own policies."] },
        { h: "6. Ads and consent", body: ["Ad partners may collect and use data to serve ads. In the EEA/UK this is subject to consent; on iOS it is subject to App Tracking Transparency (ATT). You can limit ad personalization in your device settings."] },
        { h: "7. Retention", body: ["Confession content: discarded immediately after processing. Local settings: kept on your device until you clear them. Ad/analytics data: per each partner's policy."] },
        { h: "8. Your rights", body: ["Subject to applicable law (access, deletion, correction, opt-out). Because we do not hold personal data on our servers, local data can be cleared directly on your device. Contact: support@gorocket.me."] },
        { h: "9. Children", body: ["The Service is not directed to anyone under 14."] },
        { h: "10. International transfers", body: ["Information may be processed outside your country (e.g., in the United States) for AI and advertising."] },
        { h: "11. Security", body: ["We apply reasonable safeguards, including encryption in transit."] },
        { h: "12. Changes", body: ["We will post changes in the app or on our website."] },
        { h: "13. Contact", body: ["Gorocket Company Inc. · support@gorocket.me"] },
      ],
    },
    terms: {
      title: "Terms of Service",
      effective: EFFECTIVE.en,
      intro: [],
      sections: [
        { h: "1. Acceptance", body: ["By using the Service you agree to these Terms."] },
        { h: "2. The Service", body: ['Hyuh is an entertainment service in which a fictional narrator, "the old moon," answers your confession with a long letter, a one line, and a prophecy. "The moon" is not a religion, a real deity, or astrology. All responses are for fun.'] },
        { h: "3. Not a medical or counseling service", body: ["Hyuh is not a medical, mental-health, counseling, or crisis-intervention service and is not a substitute for one. If this is an emergency or crisis, please contact a professional or an emergency hotline."] },
        { h: "4. Eligibility", body: ["You must be at least 14 to use the Service."] },
        { h: "5. Your responsibilities", body: ["You are responsible for what you enter. Do not input other people's personal information or unlawful/infringing content. Confession content is discarded after processing."] },
        { h: "6. Automated output", body: ["The letter, one line, and prophecy are AI-generated and intended for fun. They are not predictions or advice and may be inaccurate or unexpected."] },
        { h: "7. Ads and third parties", body: ["The Service contains ads and uses third-party services (advertising, AI). Third-party services are governed by their own terms."] },
        { h: "8. Intellectual property", body: ['The Service and the "Hyuh" name and logo belong to the Company. You retain rights to what you input, but that content is discarded after processing.'] },
        { h: "9. Disclaimer of warranties", body: ['The Service is provided "as is," without warranties of any kind.'] },
        { h: "10. Limitation of liability", body: ["To the extent permitted by law, the Company is not liable for indirect or incidental damages arising from use of the Service."] },
        { h: "11. Governing law", body: ["These Terms are governed by the laws of the Republic of Korea, with exclusive jurisdiction in the court of the Company's principal place of business."] },
        { h: "12. Contact", body: ["Gorocket Company Inc. · support@gorocket.me"] },
      ],
    },
  },
  ja: {
    privacy: {
      title: "プライバシーポリシー",
      effective: EFFECTIVE.ja,
      intro: ["Gorocket Company Inc.（以下「当社」）は、Hyuh（以下「本サービス」）利用者のプライバシーを尊重し、関連法令を遵守します。"],
      sections: [
        { h: "1. 基本方針", body: ["本サービスにはアカウント・ログインがなく、氏名・メールアドレス等の識別情報を収集しません。利用者が書いた、または話した告白はサーバーに保存しません。"] },
        {
          h: "2. 取り扱う情報",
          body: [
            "· 告白内容: 利用者が書いた、または音声で話したテキスト。月の手紙・一言・予言の生成と安全（危機）判定のため一時的に処理し、その後破棄します。",
            "· 音声: マイク使用時、端末のオペレーティングシステムの音声認識サービス（iOSはApple、AndroidはGoogle・端末メーカー）が音声をテキストに変換します。この過程で当該サービスが音声を自社サーバーへ送信して処理する場合があります。当社（アプリ）は音声を録音・保存・送信せず、変換後のテキストのみを処理します。",
            "· 広告・端末情報: 広告配信のため、広告識別子・IP・端末情報等が広告パートナーを通じて処理される場合があります。",
            "· ローカルデータ: 言語・サウンド設定等は利用者の端末内のみに保存します（サーバー送信なし）。",
          ],
        },
        { h: "3. 利用目的", body: ["月の手紙・一言・予言の生成、安全判定（自傷・危機の兆候を検知した場合の相談先案内）、広告配信、端末内機能の提供。"] },
        { h: "4. 保存しないこと・学習に使わないこと", body: ["告白内容は処理後に破棄し、サーバーに保存しません。AIモデルの学習には使用しません。当社は個人情報を販売しません。"] },
        {
          h: "応答の報告時に収集する情報",
          body: [
            "Hyuh は通常、ユーザーの打ち明けた内容や生成された応答を保存しません。ただし、ユーザーが特定の応答を自ら「報告」した場合に限り、サービスの品質および安全性の向上のために、以下の情報を収集・保存します。",
            "· 報告された AI 応答の本文",
            "· その応答の直前にユーザーが入力したテキスト",
            "· 報告の理由、およびユーザーが任意で記入した内容",
            "· アプリのバージョン、プラットフォーム、言語設定、報告日時",
            "この収集はユーザーが報告を送信した場合にのみ行われ、報告されなかった内容や応答は保存されません。収集した情報は AI 応答の品質および安全機能の改善のみに使用し、広告やユーザーの追跡には使用しません。Hyuh はアカウントやログインを必要としないため、この情報はユーザーの身元と結び付けられません。収集した情報は 12 か月間保管された後、削除されます。",
          ],
        },
        { h: "5. 委託先・第三者", body: ["· AI処理: Google Cloud Vertex AI — Gemini 2.5 Flash（手紙・一言の生成および安全判定）。告白テキストは当社のバックエンド（Google Cloud Run）を経由して送信され、応答後に破棄されます。", "· 音声変換（STT）: 端末OSの音声認識サービス（iOS=Apple、Android=Google・端末メーカー）。音声処理は当該OS・提供者が行い、当社は音声を保存しません。", "· 広告: Google AdMob（アプリ）／ Google AdSense（ウェブ）。", "各社の取り扱いは各社のポリシーに従います。"] },
        { h: "6. 広告と同意", body: ["広告パートナーは広告配信のため情報を収集・利用する場合があります。EEA・英国等では同意に基づき、iOSではATTに基づき処理されます。広告のパーソナライズは端末設定で制限できます。"] },
        { h: "7. 保存期間", body: ["告白内容: 処理後ただちに破棄。ローカル設定: 利用者が削除するまで端末に保存。広告・分析データ: 各パートナーのポリシーに従う。"] },
        { h: "8. 利用者の権利", body: ["関連法令に基づき、開示・訂正・削除・利用停止等を請求できます。サーバーに個人情報を保存しないため、ローカルデータは端末から直接削除できます。お問い合わせ: support@gorocket.me。"] },
        { h: "9. 子どもの個人情報", body: ["本サービスは 14 歳未満を対象としていません。"] },
        { h: "10. 国外移転", body: ["AI・広告の処理の過程で、情報が国外（米国等）で処理される場合があります。"] },
        { h: "11. 安全管理措置", body: ["通信の暗号化等、合理的な保護措置を講じます。"] },
        { h: "12. 変更", body: ["本ポリシーを変更する場合、アプリ内またはウェブサイトで告知します。"] },
        { h: "13. お問い合わせ", body: ["Gorocket Company Inc. · support@gorocket.me"] },
      ],
    },
    terms: {
      title: "利用規約",
      effective: EFFECTIVE.ja,
      intro: [],
      sections: [
        { h: "1. 同意", body: ["本サービスを利用することで、本規約に同意したものとみなします。"] },
        { h: "2. サービスの定義", body: ["Hyuhは、架空の語り手「古き月」が利用者の告白に長文の手紙・一言・予言で答えるエンターテインメントサービスです。「月」は特定の宗教・実在の神でも占いでもありません。すべての応答は娯楽のためのものです。"] },
        { h: "3. 医療・相談サービスではありません", body: ["Hyuhは医療・メンタルヘルス・カウンセリング・危機介入サービスではなく、これらに代わるものではありません。緊急時や危機的状況の場合は、専門機関または緊急相談窓口にご連絡ください。"] },
        { h: "4. 利用資格・年齢", body: ["本サービスは 14 歳以上の方のみ利用できます。"] },
        { h: "5. 利用者の責任", body: ["入力した内容について利用者が責任を負います。他人の個人情報や違法・権利侵害となる内容を入力しないでください。告白内容は処理後に破棄されます。"] },
        { h: "6. 自動生成物", body: ["手紙・一言・予言はAIが自動生成し、娯楽を目的とします。予測でも助言でもなく、不正確であったり意図と異なる場合があります。"] },
        { h: "7. 広告・第三者", body: ["本サービスには広告が含まれ、第三者サービス（広告・AI等）を利用します。第三者サービスには各社の規約・ポリシーが適用されます。"] },
        { h: "8. 知的財産権", body: ["本サービス、「Hyuh」の名称・ロゴ等の一切の権利は当社に帰属します。利用者が入力した内容の権利は利用者に帰属しますが、当該内容は処理後に破棄されます。"] },
        { h: "9. 保証の否認", body: ["本サービスは「現状有姿」で提供され、いかなる保証も行いません。"] },
        { h: "10. 責任の制限", body: ["法令が認める範囲において、当社は本サービスの利用に起因する間接・付随的損害について責任を負いません。"] },
        { h: "11. 準拠法・管轄", body: ["本規約は大韓民国法を準拠法とし、当社の本店所在地を管轄する裁判所を専属的合意管轄裁判所とします。"] },
        { h: "12. お問い合わせ", body: ["Gorocket Company Inc. · support@gorocket.me"] },
      ],
    },
  },
};
